import { DeliveryType, MenuItemDto, SiteDiscountCodeQueryResponse } from '@models/ropolo';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CartItem, CartState, OrderItemToppings, UpdateOrderItemProps } from './types';
import { findItemIndex, findSameItems, isSameToppings, updateItemToppings } from './utils';

const initialState: CartState = {
  vendorId: undefined,
  vendorName: undefined,
  deliveryType: undefined,
  addressId: undefined,
  items: [],
  discountCode: undefined,
  tableNumber: undefined,
  tableName: undefined,
  numberOfPerson: 0,
  fetchAddressLoading: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<CartState>) => ({
      ...initialState,
      vendorId: action.payload.vendorId,
      vendorName: action.payload.vendorName,
      items: action.payload.items,
    }),
    createOrder: (state, action) => {
      const { id, orderItemToppings, withTax } = action.payload;
      const sameItems = findSameItems(state.items, id);
      const newItems = [...state.items];
      let isDuplicated = false;

      sameItems.forEach((same) => {
        const sameToppings = same.orderItemToppings ?? [];
        const payloadToppings = orderItemToppings ?? [];

        if (isSameToppings(sameToppings, payloadToppings)) {
          const index = findItemIndex(state.items, same.temporaryOrderId);
          const updatedToppings = updateItemToppings(newItems[index].orderItemToppings ?? []);

          newItems[index] = {
            ...newItems[index],
            count: same.count + 1,
            withTax,
            orderItemToppings: updatedToppings,
          };
          isDuplicated = true;
        }
      });

      if (!isDuplicated) newItems.push(action.payload);

      return {
        ...state,
        items: newItems,
      };
    },
    updateOrderItem: (state, action: PayloadAction<UpdateOrderItemProps>) => {
      const { temporaryOrderId, count } = action.payload;

      const items = state.items
        // eslint-disable-next-line no-confusing-arrow
        .map((item) =>
          item.temporaryOrderId === temporaryOrderId
            ? {
                ...item,
                count,
                orderItemToppings: item.orderItemToppings?.map((topping) => ({
                  ...topping,
                  count,
                })),
              }
            : item
        )
        .filter((item) => item.count > 0 && item.available && item.active);

      return {
        ...state,
        items,
      };
    },

    clearCart: (state) => ({
      ...state,
      items: [],
    }),
    setDeliveryType: (state, action: PayloadAction<DeliveryType | undefined>) => ({
      ...state,
      deliveryType: action.payload,
    }),
    setTableNumber: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      tableNumber: action.payload,
    }),
    setFetchAddressLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      fetchAddressLoading: action.payload,
    }),
    setTableName: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      tableName: action.payload,
    }),
    setAddress: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      addressId: action.payload,
    }),
    setDiscountCode: (
      state,
      action: PayloadAction<(SiteDiscountCodeQueryResponse & { freeItems: MenuItemDto[] }) | undefined>
    ) => ({
      ...state,
      discountCode: action.payload,
    }),
    setNumberOfPerson: (state, action: PayloadAction<number | undefined>) => ({
      ...state,
      numberOfPerson: action.payload || 0,
    }),
  },
});

export type { CartItem, OrderItemToppings };
export default cartSlice;
